import { useState, useEffect, useCallback, useRef } from 'react';
import { Procedure, TestPanel, TestDefinition } from '../../../types/procedures';
import { ProcedureService } from '../../../services/procedureService';
import { getPatient } from '../../../services/patientService';
import { useSelector } from 'react-redux';
import { selectUserFacilities } from '../../../redux/slices/facilitiesSlice';

import { convertTimestampsToISO } from '../../../utils/firebaseUtils';
import { registerCache, unregisterCache } from '../../../utils/cacheUtils';
import { useUserData } from '../../../hooks/useUserData';

const procedureService = new ProcedureService();

// Cache for patient-specific data
const patientDataCache = new Map<string, {
  procedures: Procedure[];
  testPanels: TestPanel[];
  testDefinitions: TestDefinition[];
  patientMap: { [id: string]: { name: string, dateOfBirth: string } };
  labIntegrationMap: { [id: string]: string };
  timestamp: number;
  patientId: string;
}>();

const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

export const useProcedures = (patientId?: string) => {
  const { defaultFacility } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [testPanels, setTestPanels] = useState<TestPanel[]>([]);
  const [testDefinitions, setTestDefinitions] = useState<TestDefinition[]>([]);
  const [patientMap, setPatientMap] = useState<{ [id: string]: { name: string, dateOfBirth: string } }>({});
  const [labIntegrationMap, setLabIntegrationMap] = useState<{ [id: string]: string }>({});
  const [labsLoaded, setLabsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Track the current patient ID to prevent stale data
  const currentPatientIdRef = useRef<string | undefined>(patientId);
  const lastFetchTimeRef = useRef<number>(0);

  // Create cache object for registration
  const cacheObject = useRef({
    clear: () => {
      console.log('useProcedures: Clearing all caches');
      patientDataCache.clear();
      setProcedures([]);
      setTestPanels([]);
      setTestDefinitions([]);
      setPatientMap({});
      setLabIntegrationMap({});
      setLabsLoaded(false);
    },
    clearForPatient: (targetPatientId: string) => {
      if (targetPatientId === patientId) {
        console.log(`useProcedures: Clearing cache for patient ${targetPatientId}`);
        patientDataCache.delete(`patient_${targetPatientId}`);
        setProcedures([]);
        setTestPanels([]);
        setTestDefinitions([]);
        setPatientMap({});
        setLabIntegrationMap({});
        setLabsLoaded(false);
      }
    },
    clearForEncounter: (encounterId: string) => {
      // Clear cache if the encounter is related to our current patient
      if (patientId && procedures.some(p => p.encounterId === encounterId)) {
        console.log(`useProcedures: Clearing cache for encounter ${encounterId}`);
        patientDataCache.delete(`patient_${patientId}`);
        setProcedures([]);
        setTestPanels([]);
        setTestDefinitions([]);
        setPatientMap({});
        setLabIntegrationMap({});
        setLabsLoaded(false);
      }
    }
  });

  // Register cache on mount
  useEffect(() => {
    const cacheName = `useProcedures_${patientId || 'global'}`;
    registerCache(cacheName, cacheObject.current);
    
    return () => {
      unregisterCache(cacheName);
    };
  }, [patientId]);



  // Memoize the addProcedure function
  const addProcedure = useCallback((newProcedure: Procedure) => {
    // Convert any Firestore Timestamps to ISO strings for safety
    const safeProcedure = convertTimestampsToISO(newProcedure);
    setProcedures(prev => [safeProcedure, ...prev]);
    
    // Update cache if we have a patient ID
    if (patientId) {
      const cacheKey = `patient_${patientId}`;
      const cached = patientDataCache.get(cacheKey);
      if (cached) {
        cached.procedures = [safeProcedure, ...cached.procedures];
        cached.timestamp = Date.now();
        patientDataCache.set(cacheKey, cached);
      }
    }
  }, [patientId]);

  // Memoize the loadTestDefinitionsByOrderType function
  const loadTestDefinitionsByOrderType = useCallback(async (orderType: string): Promise<TestDefinition[]> => {
    try {
      return await procedureService.getTestDefinitionsByOrderType(orderType);
    } catch (error) {
      console.error('Error loading test definitions by order type:', error);
      return [];
    }
  }, []);

  // Check if cache is valid for current patient
  const isCacheValid = useCallback((cacheKey: string) => {
    const cached = patientDataCache.get(cacheKey);
    if (!cached) return false;
    
    // Check if cache is for the same patient and not expired
    return cached.patientId === patientId && 
           (Date.now() - cached.timestamp) < CACHE_EXPIRY;
  }, [patientId]);

  // Load data for a specific patient
  const loadPatientData = useCallback(async (targetPatientId: string) => {
    if (!targetPatientId) {
      console.warn('useProcedures: Cannot load data without patientId');
      setLoading(false);
      return;
    }

    const cacheKey = `patient_${targetPatientId}`;
    
    // Check if we have valid cached data
    if (isCacheValid(cacheKey)) {
      const cached = patientDataCache.get(cacheKey)!;
      console.log(`useProcedures: Using cached data for patient ${targetPatientId}`);
      setProcedures(cached.procedures);
      setTestPanels(cached.testPanels);
      setTestDefinitions(cached.testDefinitions);
      setPatientMap(cached.patientMap);
      setLabIntegrationMap(cached.labIntegrationMap);
      setLabsLoaded(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log(`useProcedures: Loading fresh data for patient ${targetPatientId}`);
      
      // Load patient-specific procedures
      const [proceduresData, panelsData, definitionsData] = await Promise.all([
        procedureService.getPatientProcedures(targetPatientId), // Always pass patientId
        procedureService.getTestPanels(),
        procedureService.getTestDefinitions(),
      ]);
      
      const validatedProcedures = Array.isArray(proceduresData) 
        ? proceduresData.filter(proc => proc && typeof proc === 'object')
        : [];
      
      const withIds = validatedProcedures.map((proc: any, idx: number) => ({
        ...proc,
        id: proc.id || proc.patientId || idx.toString(),
      }));
      
      // Convert any remaining Firestore Timestamps to ISO strings
      const safeProcedures = withIds.map(proc => convertTimestampsToISO(proc));
      setProcedures(safeProcedures);
      setTestPanels(panelsData || []);
      setTestDefinitions(definitionsData || []);
      
      // Load patient data for procedures
      const uniquePatientIds = Array.from(new Set(withIds.map(p => p.patientId).filter(Boolean)));
      const patientEntries = await Promise.all(
        uniquePatientIds.map(async (id) => {
          try {
            const patient = await getPatient(id);
            return [id, patient ? { name: (patient as any).name, dateOfBirth: (patient as any).dateOfBirth } : { name: id, dateOfBirth: '' }];
          } catch (error) {
            console.error(`Error loading patient ${id}:`, error);
            return [id, { name: id, dateOfBirth: '' }];
          }
        })
      );
      
      const newPatientMap = Object.fromEntries(patientEntries);
      setPatientMap(newPatientMap);
      setLabIntegrationMap({});
      setLabsLoaded(true);
      
      // Cache the data
      patientDataCache.set(cacheKey, {
        procedures: safeProcedures,
        testPanels: panelsData || [],
        testDefinitions: definitionsData || [],
        patientMap: newPatientMap,
        labIntegrationMap: {},
        timestamp: Date.now(),
        patientId: targetPatientId
      });
      
      lastFetchTimeRef.current = Date.now();
      
    } catch (error) {
      console.error('Error loading patient data:', error);
      setProcedures([]);
      setTestPanels([]);
      setTestDefinitions([]);
      setPatientMap({});
      setLabIntegrationMap({});
      setLabsLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [isCacheValid]);

  // Load global data (lab integrations, test panels, test definitions) when no patientId is provided
  const loadGlobalData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('useProcedures: Loading global data for general procedures page');
      
      // Get the user's default facility ID
      const defaultFacilityId = defaultFacility?.id;
      
      if (!defaultFacilityId) {
        console.warn('useProcedures: No facility ID available, cannot load procedures');
        setProcedures([]);
        setPatientMap({});
        setLabsLoaded(true);
        setLoading(false);
        return;
      }
      
      // Load global data that doesn't require a specific patient
      // Pass facility ID to filter lab integrations based on user's facility access
      const [panelsData, definitionsData, labIntegrationsData, proceduresData] = await Promise.all([
        procedureService.getTestPanels(),
        procedureService.getTestDefinitions(),
        procedureService.getLabIntegrations(), // Backend auto-filters based on user role & facility access
        procedureService.getAllProcedures(defaultFacilityId), // Get all procedures for the user's facility
      ]);
      
      setTestPanels(panelsData || []);
      setTestDefinitions(definitionsData || []);
      
      // Create lab integration map
      const labIntegrationMap: { [id: string]: string } = {};
      if (Array.isArray(labIntegrationsData)) {
        labIntegrationsData.forEach(integration => {
          labIntegrationMap[integration.id] = integration.name;
        });
      }
      setLabIntegrationMap(labIntegrationMap);
      
      // Set procedures data for the general page
      const validatedProcedures = Array.isArray(proceduresData) 
        ? proceduresData.filter(proc => proc && typeof proc === 'object')
        : [];
      
      const withIds = validatedProcedures.map((proc: any, idx: number) => ({
        ...proc,
        id: proc.id || proc.patientId || idx.toString(),
      }));
      
      // Convert any remaining Firestore Timestamps to ISO strings
      const safeProcedures = withIds.map(proc => convertTimestampsToISO(proc));
      setProcedures(safeProcedures);
      
      // Load patient data for procedures
      const uniquePatientIds = Array.from(new Set(withIds.map(p => p.patientId).filter(Boolean)));
      const patientEntries = await Promise.all(
        uniquePatientIds.map(async (id) => {
          try {
            const patient = await getPatient(id);
            return [id, patient ? { name: (patient as any).name, dateOfBirth: (patient as any).dateOfBirth } : { name: id, dateOfBirth: '' }];
          } catch (error) {
            console.error(`Error loading patient ${id}:`, error);
            return [id, { name: id, dateOfBirth: '' }];
          }
        })
      );
      
      const newPatientMap = Object.fromEntries(patientEntries);
      setPatientMap(newPatientMap);
      
      // Set labsLoaded to true so the component can render
      setLabsLoaded(true);
      
    } catch (error) {
      console.error('Error loading global data:', error);
      setTestPanels([]);
      setTestDefinitions([]);
      setLabIntegrationMap({});
      setProcedures([]);
      setPatientMap({});
      setLabsLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [defaultFacility]);

  // Clear cache for a specific patient (useful when data becomes stale)
  const clearPatientCache = useCallback((targetPatientId: string) => {
    if (targetPatientId) {
      const cacheKey = `patient_${targetPatientId}`;
      patientDataCache.delete(cacheKey);
      console.log(`useProcedures: Cleared cache for patient ${targetPatientId}`);
    }
  }, []);

  // Main effect to load data when patientId changes
  useEffect(() => {
    // Clear previous data when patient changes
    if (currentPatientIdRef.current !== patientId) {
      console.log(`useProcedures: Patient changed from ${currentPatientIdRef.current} to ${patientId}`);
      setProcedures([]);
      setTestPanels([]);
      setTestDefinitions([]);
      setPatientMap({});
      setLabIntegrationMap({});
      setLabsLoaded(false);
      currentPatientIdRef.current = patientId;
    }

    if (patientId) {
      loadPatientData(patientId);
    } else {
      // Load global data for general procedures page
      console.log('useProcedures: No patientId provided, loading global data for general procedures page');
      loadGlobalData();
    }
  }, [patientId, loadPatientData, loadGlobalData]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up when component unmounts
      if (patientId) {
        const cacheKey = `patient_${patientId}`;
        const cached = patientDataCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) > CACHE_EXPIRY) {
          patientDataCache.delete(cacheKey);
        }
      }
    };
  }, [patientId]);

  return {
    procedures,
    testPanels,
    testDefinitions,
    patientMap,
    labIntegrationMap,
    labsLoaded,
    loading,
    addProcedure,
    loadTestDefinitionsByOrderType,
    clearPatientCache,
    refreshData: () => {
      if (patientId) {
        loadPatientData(patientId);
      } else {
        // For global data, we need to get the facility ID
        const defaultFacilityId = defaultFacility?.id;
        if (defaultFacilityId) {
          loadGlobalData();
        } else {
          console.warn('useProcedures: Cannot refresh global data without facility ID');
        }
      }
    }
  };
}; 